import { z } from 'zod';
import type { Rol } from '../roles';

/**
 * El guardia entra con teléfono o con correo, lo que tenga. Muchos guardias no
 * usan correo, y obligarlos a inventarse uno para poder trabajar es la clase de
 * fricción que hace que un turno completo se registre con la cuenta del
 * supervisor.
 */
export const iniciarSesionSchema = z.object({
  identificador: z.string().trim().min(1, 'Escribe tu correo o teléfono'),
  password: z.string().min(1, 'Escribe tu contraseña'),
});
export type IniciarSesion = z.infer<typeof iniciarSesionSchema>;

export const cambiarPasswordSchema = z
  .object({
    passwordActual: z.string().min(1),
    passwordNueva: z.string().min(10, 'Mínimo 10 caracteres'),
  })
  .refine((d) => d.passwordActual !== d.passwordNueva, {
    message: 'La contraseña nueva debe ser distinta de la actual',
    path: ['passwordNueva'],
  });
export type CambiarPassword = z.infer<typeof cambiarPasswordSchema>;

/** Lo que la sesión expone al cliente. Nunca incluye `passwordHash`. */
export interface UsuarioSesion {
  id: string;
  nombre: string;
  apellidoPaterno: string | null;
  apellidoMaterno: string | null;
  email: string | null;
  telefono: string | null;
  rol: Rol;
  /**
   * Los condominios a los que pertenece la cuenta.
   *
   * Es un arreglo y no un `condominioId` porque la pregunta «¿de qué
   * condominio es esta operación?» **no siempre tiene la misma respuesta que
   * "el del usuario"**, y cuando el campo era uno solo esa confusión no se
   * veía. Ocho avisos de tiempo real se estaban mandando al condominio del
   * guardia en vez de al del acceso; con un solo condominio por servidor daba
   * igual, en la nube habría mandado la tarjeta de una visita al
   * fraccionamiento equivocado.
   *
   * Hoy trae **cero o un** elemento: cero para el SUPER_ADMIN de Kodica, uno
   * para todos los demás. Quien necesite *el* condominio de la sesión usa
   * `exigirCondominio`, que falla si no hay exactamente uno.
   */
  condominios: readonly string[];
  /**
   * Entró con una contraseña provisional y tiene que elegir la suya antes de
   * hacer nada. Viaja en la sesión —y no se deduce en el cliente— porque quien
   * manda sobre esto es el servidor: la app solo obedece.
   */
  debeCambiarPassword?: boolean;
}

export interface RespuestaSesion {
  token: string;
  /** ISO 8601. El cliente lo usa para avisar antes de que caduque el turno. */
  expiraEn: string;
  usuario: UsuarioSesion;
}

/**
 * Un condominio al que esta sesión puede entrar.
 *
 * Para casi todo el mundo la lista tiene un elemento —el suyo— y la app ni la
 * enseña. Para el SUPER_ADMIN de Kodica son todos: es quien opera la
 * plataforma, y sin esto no puede entrar a ninguno, porque no pertenece a
 * ninguno y `exigirCondominio` lo rechaza con razón.
 */
export interface CondominioDisponible {
  id: string;
  nombre: string;
  slug: string;
}

// Aquí hubo un `activo: boolean` y se quitó a propósito. Lo calculaba el
// servidor con la sesión del momento en que se preguntó, así que en cuanto
// alguien cambiaba de fraccionamiento esa respuesta ya era vieja — y el
// selector de la barra lateral se quedaba diciendo «Sin fraccionamiento»
// después de elegir uno.
//
// Cuál está activo lo sabe la sesión (`usuario.condominios`), que se actualiza
// con el token nuevo. Un campo que responde a la misma pregunta desde una foto
// más vieja no es redundante: es el que alguien va a usar por ser el que está
// más a mano.

/** Entrar a un condominio: cambia el activo de la sesión y da un token nuevo. */
export const entrarACondominioSchema = z.object({
  condominioId: z.string().cuid(),
});
export type EntrarACondominio = z.infer<typeof entrarACondominioSchema>;
